const API_BASE = '/api';

interface ApiOptions {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  skipAuth?: boolean;
  skipOrg?: boolean;
}

function isDemoMode(): boolean {
  const token = localStorage.getItem('hr_access_token');
  return token?.startsWith('demo-') ?? false;
}

async function handleDemoRequest<T>(endpoint: string, options: ApiOptions): Promise<T> {
  const {
    method = 'GET',
    body,
  } = options;

  const {
    mockVacancies,
    mockVacancyStats,
    mockFunnel,
    mockCandidates,
    mockCandidateDetails,
    mockScoring,
    mockHHChat,
    mockEmployerProfiles,
    mockEmployerQuestions,
    mockEmailTemplates,
    mockEmailLog,
    mockHHAccounts,
    mockShareLinks,
    mockOrganizations,
    mockMembers,
    mockUsers,
    mockPublicVacancies,
    mockPublicVacancy,
  } = await import('@/mocks/api');

  // Simulate network delay
  await new Promise((resolve) => { setTimeout(resolve, 300 + Math.random() * 400); });

  // Match endpoint patterns
  const vacancyIdMatch = endpoint.match(/^\/vacancies\/([^/]+)$/);
  const vacancyStatsMatch = endpoint.match(/^\/vacancies\/([^/]+)\/stats$/);
  const vacancyFunnelMatch = endpoint.match(/^\/vacancies\/([^/]+)\/funnel$/);
  const vacancyCandidatesMatch = endpoint.match(/^\/vacancies\/([^/]+)\/candidates$/);
  const candidateIdMatch = endpoint.match(/^\/candidates\/([^/]+)$/);
  const scoringMatch = endpoint.match(/^\/scoring\/([^/]+)$/);
  const hhChatMatch = endpoint.match(/^\/candidates\/([^/]+)\/hh-chat$/);
  const hhChatSendMatch = endpoint.match(/^\/candidates\/([^/]+)\/hh-chat\/send$/);
  const shareLinksMatch = endpoint.match(/^\/share\/links\/([^/]+)$/);
  const orgMembersMatch = endpoint.match(/^\/organizations\/([^/]+)\/members$/);
  const publicVacancyMatch = endpoint.match(/^\/public\/vacancy\/(.+)$/);
  const questionnaireLinkMatch = endpoint.match(/^\/candidates\/([^/]+)\/questionnaire-link$/);
  const vacancyCriteriaMatch = endpoint.match(/^\/vacancies\/([^/]+)\/criteria$/);
  const candidateStatusMatch = endpoint.match(/^\/candidates\/([^/]+)\/status$/);

  if (method === 'GET' && endpoint === '/vacancies') {
    return mockVacancies as T;
  }

  if (method === 'GET' && vacancyIdMatch) {
    const id = vacancyIdMatch[1];
    const vacancy = mockVacancies.find((v: { id: string }) => v.id === id);
    if (!vacancy) throw new Error('Вакансия не найдена');
    return vacancy as T;
  }

  if (method === 'GET' && vacancyStatsMatch) {
    const id = vacancyStatsMatch[1];
    return (mockVacancyStats[id] || { total: 0, by_status: {} }) as T;
  }

  if (method === 'GET' && vacancyFunnelMatch) {
    const id = vacancyFunnelMatch[1];
    return (mockFunnel[id] || { total: 0, invited: 0, responded: 0, answered: 0 }) as T;
  }

  if (method === 'GET' && vacancyCandidatesMatch) {
    const id = vacancyCandidatesMatch[1];
    return (mockCandidates[id] || []) as T;
  }

  if (method === 'GET' && candidateIdMatch) {
    const id = candidateIdMatch[1];
    const detail = mockCandidateDetails[id];
    if (!detail) throw new Error('Кандидат не найден');
    return detail as T;
  }

  if (method === 'GET' && scoringMatch) {
    const id = scoringMatch[1];
    const scoring = mockScoring[id];
    if (!scoring) {
      // Return empty scoring for candidates without explicit data
      return {
        resume_score: { total_score: 0, max_score: 100, criteria_scores: {}, criteria_reasoning: {}, ai_summary: 'Оценка не проводилась', red_flags: [], strengths: [] },
        answers_score: { total_score: 0, max_score: 100, criteria_scores: {}, criteria_reasoning: {}, ai_summary: 'Оценка не проводилась', red_flags: [], strengths: [] },
        compatibility_score: { total_score: 0, max_score: 100, criteria_scores: {}, criteria_reasoning: {}, ai_summary: 'Оценка не проводилась', red_flags: [], strengths: [] },
      } as T;
    }
    return scoring as T;
  }

  if (method === 'GET' && hhChatMatch) {
    const id = hhChatMatch[1];
    return (mockHHChat[id] || []) as T;
  }

  if (method === 'POST' && hhChatSendMatch) {
    const id = hhChatSendMatch[1];
    const bodyData = body as { text?: string };
    if (!mockHHChat[id]) mockHHChat[id] = [];
    mockHHChat[id].push({
      id: `msg-${Date.now()}`,
      author: 'employer',
      text: bodyData.text || '',
      sent_at: new Date().toISOString(),
    });
    return { success: true } as T;
  }

  if (method === 'GET' && endpoint === '/employer-profile/questions') {
    return mockEmployerQuestions as T;
  }

  if (method === 'GET' && endpoint === '/employer-profiles') {
    return mockEmployerProfiles as T;
  }

  if (method === 'GET' && endpoint === '/email-templates') {
    return mockEmailTemplates as T;
  }

  if (method === 'GET' && endpoint === '/emails/log') {
    return mockEmailLog as T;
  }

  if (method === 'GET' && endpoint === '/hh/accounts') {
    return mockHHAccounts as T;
  }

  if (method === 'GET' && shareLinksMatch) {
    return mockShareLinks as T;
  }

  if (method === 'GET' && endpoint === '/organizations') {
    return mockOrganizations as T;
  }

  if (method === 'GET' && orgMembersMatch) {
    return mockMembers as T;
  }

  if (method === 'GET' && endpoint === '/users') {
    return mockUsers as T;
  }

  if (method === 'GET' && endpoint === '/public/jobs') {
    return mockPublicVacancies as T;
  }

  if (method === 'GET' && publicVacancyMatch) {
    const slug = publicVacancyMatch[1];
    const result = mockPublicVacancy(slug);
    if (!result) throw new Error('Вакансия не найдена');
    return result as T;
  }

  if (method === 'POST' && questionnaireLinkMatch) {
    return { link: 'https://starec.ai/questionnaire/demo-token' } as T;
  }

  if (method === 'PUT' && vacancyCriteriaMatch) {
    return { success: true } as T;
  }

  if (method === 'PUT' && candidateStatusMatch) {
    return { success: true } as T;
  }

  // Generic success for any POST/PUT/PATCH/DELETE not handled above
  if (method !== 'GET') {
    return { success: true } as T;
  }

  // For unhandled GET, return empty
  return [] as unknown as T;
}

// Ошибки FastAPI приходят и строкой, и списком ошибок валидации —
// без разбора пользователь видел «[object Object]».
function formatApiError(err: unknown, status: number): string {
  const detail = (err as { detail?: unknown } | null)?.detail;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    const parts = detail
      .map((d) => {
        const item = d as { msg?: string; loc?: unknown[] };
        const field = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : '';
        return field ? `${field}: ${item.msg || ''}` : item.msg || '';
      })
      .filter(Boolean);
    if (parts.length) return parts.join('; ');
  }
  if (detail) return JSON.stringify(detail);
  return `HTTP ${status}`;
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    skipAuth = false,
    skipOrg = false,
  } = options;

  // Demo mode intercept
  if (!skipAuth && isDemoMode()) {
    return handleDemoRequest<T>(endpoint, options);
  }

  const token = skipAuth ? null : localStorage.getItem('hr_access_token');
  const orgId = skipOrg ? null : localStorage.getItem('hr_org_id');

  const fetchHeaders: Record<string, string> = {
    'Content-Type': 'application/json',
    ...headers,
  };

  if (token) {
    fetchHeaders.Authorization = `Bearer ${token}`;
  }
  if (orgId) {
    fetchHeaders['X-Organization-Id'] = orgId;
  }

  const res = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers: fetchHeaders,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (res.status === 401 && !skipAuth) {
    const refresh = localStorage.getItem('hr_refresh_token');
    if (refresh) {
      const refreshRes = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refresh }),
      });
      if (refreshRes.ok) {
        const data = await refreshRes.json();
        localStorage.setItem('hr_access_token', data.access_token);
        // Refresh-токен перезаписываем, только если сервер его вернул: иначе
        // в хранилище попадала строка "undefined" и следующее обновление
        // токена падало с «Not authenticated».
        if (data.refresh_token) {
          localStorage.setItem('hr_refresh_token', data.refresh_token);
        }
        // retry original request
        return apiRequest(endpoint, options);
      }
    }
    // refresh failed — clear auth
    localStorage.removeItem('hr_access_token');
    localStorage.removeItem('hr_refresh_token');
    window.dispatchEvent(new CustomEvent('auth:logout'));
  }

  if (!res.ok) {
    const err = await res.json().catch(() => null);
    throw new Error(formatApiError(err, res.status));
  }

  if (res.status === 204) {
    return {} as T;
  }

  return res.json();
}

// API helpers
export const api = {
  get: <T,>(endpoint: string, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'GET', headers }),
  post: <T,>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'POST', body, headers }),
  put: <T,>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'PUT', body, headers }),
  patch: <T,>(endpoint: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'PATCH', body, headers }),
  del: <T,>(endpoint: string, headers?: Record<string, string>) =>
    apiRequest<T>(endpoint, { method: 'DELETE', headers }),
  // публичные страницы (анкета, запись на интервью) — без токена и без org-заголовка
  publicGet: <T,>(endpoint: string) =>
    apiRequest<T>(endpoint, { method: 'GET', skipAuth: true, skipOrg: true }),
  publicPost: <T,>(endpoint: string, body?: unknown) =>
    apiRequest<T>(endpoint, { method: 'POST', body, skipAuth: true, skipOrg: true }),
  // raw fetch for multipart uploads
  raw: (endpoint: string, init: Parameters<typeof fetch>[1], skipAuth?: boolean) => {
    if (!skipAuth && isDemoMode()) {
      // Return a fake Response for demo mode raw requests
      return Promise.resolve(new Response(JSON.stringify({ success: true }), { status: 200, headers: { 'Content-Type': 'application/json' } }));
    }
    const token = skipAuth ? null : localStorage.getItem('hr_access_token');
    const orgId = skipAuth ? null : localStorage.getItem('hr_org_id');
    const h = new Headers(init.headers);
    if (token) h.set('Authorization', `Bearer ${token}`);
    if (orgId) h.set('X-Organization-Id', orgId);
    return fetch(`${API_BASE}${endpoint}`, { ...init, headers: h });
  },
};

export { API_BASE };